import {
    Box,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { IProdutoFormData } from ".";
import SelectSetor from "../../../../../components/SelectsSimpleApiQuery/SelectSetor";
import SelectDepartamentoEstoqueMultiple
    from "../../../../../components/SelectsSimpleApiQuery/SelectDepartamentoEstoque/Multiple";
import { validateEAN13 } from "../../../../../helpers/validateEAN13";
import { useMemo } from "react";
import { formatCurrencyCategory } from "../../../../../helpers/masks";
import SelectCategoriaProduto from "../../../../../components/SelectsSimpleApiQuery/SelectCategoriaProduto";
import { CategoriaHierarquica } from "../ModalDetalheProdutoContext/DialogDetalheProduto/AdicionarCategoriaModal";
export interface Atributo {
    id: number;
    categoria_id?: number;
    nome: string;
    compoe_nome: boolean;
    obrigatorio: boolean;
    tipo: string;
    ordem_nome: number
}

export default function Step01() {

    const { control, formState: { errors }, setValue } = useFormContext<IProdutoFormData>();

    const selectedCategory = useWatch({
        control,
        name: "informacoesBasicas.categoria_data"
    });
    const atributos = useWatch({
        control,
        name: "informacoesBasicas.atributos"
    });

    const nomeGerado = useMemo(() => {
        if (selectedCategory?.atributos && Array.isArray(selectedCategory.atributos) && atributos) {
            const atributosQueCompoem = selectedCategory.atributos
                .filter((attr: Atributo) => attr.compoe_nome)
                .sort((a: Atributo, b: Atributo) => (a.ordem_nome || 0) - (b.ordem_nome || 0));

            if (atributosQueCompoem.length > 0) {
                const partesNome = atributosQueCompoem
                    .map((attr: Atributo) => {
                        const valor = atributos[attr.nome];
                        return valor && String(valor).trim() ? String(valor).trim() : null;
                    })
                    .filter(Boolean);

                return partesNome.join(" ");
            }
        }
        return "";
    }, [atributos, selectedCategory]);

    const handleCategoriaAdicionada = (
        id: number | null,
        caminhoFormatado: string = '',
        categoria?: CategoriaHierarquica
    ) => {
        setValue('informacoesBasicas.categoria_id', id);
        setValue('informacoesBasicas.categoria_nome', caminhoFormatado);
        if (categoria) {
            setValue('informacoesBasicas.categoria_data', categoria);
        }
    };

    return (
        <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
                Informações Básicas do Produto
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure as informações fundamentais do produto como nome, descrição e categoria.
            </Typography>

            <Grid container spacing={2}>


                <Grid size={{ xs: 12 }}>
                    <Controller
                        name="informacoesBasicas.categoria_id"
                        control={control}
                        rules={{ required: "Categoria é obrigatória" }}
                        render={({ field }) => (
                            <SelectCategoriaProduto
                                value={field.value}
                                onChange={handleCategoriaAdicionada}
                                required
                                error={!!errors.informacoesBasicas?.categoria_id}
                                helperText={errors.informacoesBasicas?.categoria_id?.message}
                            />
                        )}
                    />
                </Grid>

                {selectedCategory?.atributos && selectedCategory.atributos.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" gutterBottom mb={2}>
                            Atributos da Categoria
                        </Typography>

                        <Grid container spacing={2}>
                            {selectedCategory.atributos.map((atributo: Atributo) => (
                                <Grid size={{ xs: 12, md: 6 }} key={atributo.id}>
                                    <Controller
                                        name={`informacoesBasicas.atributos.${atributo.nome}` as any}
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            required: atributo.obrigatorio ? `${atributo.nome} é obrigatório` : false,
                                            validate: (value) => {
                                                if (atributo.obrigatorio && (!value || (typeof value === 'string' && !value.trim()))) {
                                                    return `${atributo.nome} é obrigatório`;
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState }) => {
                                            const isMoeda = atributo.tipo === "moeda";
                                            const isData = atributo.tipo === "data";

                                            return (
                                                <TextField
                                                    label={atributo.nome}
                                                    fullWidth
                                                    required={atributo.obrigatorio}
                                                    error={!!fieldState.error}
                                                    {...(atributo.compoe_nome ? {
                                                        helperText: 'Este atributo compoe o nome do produto'
                                                    } : { helperText: fieldState.error?.message })}
                                                    type={isData ? "date" : "text"}
                                                    value={field.value || ""}
                                                    onChange={(e) => {
                                                        if (isMoeda) {
                                                            field.onChange(formatCurrencyCategory(e.target.value));
                                                        } else {
                                                            field.onChange(e.target.value);
                                                        }
                                                    }}
                                                    InputLabelProps={
                                                        isData
                                                            ? { shrink: true, required: atributo.obrigatorio }
                                                            : { required: atributo.obrigatorio }
                                                    }
                                                    inputProps={
                                                        isMoeda
                                                            ? { inputMode: "numeric" }
                                                            : undefined
                                                    }
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="informacoesBasicas.nome"
                        control={control}
                        render={({ field }) => {
                            if (field.value !== nomeGerado) {
                                setValue("informacoesBasicas.nome", nomeGerado, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }

                            return (
                                <TextField
                                    label="Nome do produto"
                                    fullWidth
                                    disabled
                                    value={nomeGerado}
                                    helperText="O nome é composto pelos atributos selecionados"
                                />
                            );
                        }}
                    />

                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="informacoesBasicas.codigo_barra"
                        control={control}
                        rules={{
                            validate: (value) => {
                                if (value) {
                                    const stringValue = String(value).padStart(13, "0");
                                    return validateEAN13(stringValue) || "Código de barra EAN inválido";
                                }
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Código de barra"
                                fullWidth
                                slotProps={{ htmlInput: { maxLength: 13 } }}
                                error={!!errors.informacoesBasicas?.codigo_barra}
                                helperText={errors.informacoesBasicas?.codigo_barra?.message}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <Controller
                        name="informacoesBasicas.descricao"
                        control={control}
                        rules={{ required: "Descrição é obrigatória" }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Descrição Complementar"
                                fullWidth
                                multiline
                                minRows={3}
                                required
                                slotProps={{ htmlInput: { maxLength: 245 } }}
                                error={!!errors.informacoesBasicas?.descricao}
                                helperText={errors.informacoesBasicas?.descricao?.message}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="informacoesBasicas.departamento"
                        control={control}
                        rules={{ required: "Departamento é obrigatório" }}
                        render={({ field }) => (
                            <SelectDepartamentoEstoqueMultiple
                                value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                onChange={field.onChange}
                                multiple={false}
                                required
                                error={!!errors.informacoesBasicas?.departamento}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="informacoesBasicas.setor"
                        control={control}
                        rules={{ required: "Setor é obrigatório" }}
                        render={({ field }) => (
                            <SelectSetor
                                value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                onChange={field.onChange}
                                required
                                error={!!errors.informacoesBasicas?.setor}
                                multiple={false}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}